import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type MouseEvent as ReactMouseEvent,
	type ReactNode
} from 'react';

type AlertDialogOptions = {
	title?: string;
	confirmLabel?: string;
};

type AlertDialogContextValue = {
	showAlert: (message: string, options?: AlertDialogOptions) => void;
	hideAlert: () => void;
};

type AlertDialogState = {
	isOpen: boolean;
	title: string;
	message: string;
	confirmLabel: string;
};

const AlertDialogContext = createContext<AlertDialogContextValue | undefined>(undefined);

const defaultState: AlertDialogState = {
	isOpen: false,
	title: 'Notice',
	message: '',
	confirmLabel: 'OK'
};

export function AlertDialogProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<AlertDialogState>(defaultState);

	const showAlert = useCallback((message: string, options?: AlertDialogOptions) => {
		setState({
			isOpen: true,
			message,
			title: options?.title ?? 'Notice',
			confirmLabel: options?.confirmLabel ?? 'OK'
		});
	}, []);

	const hideAlert = useCallback(() => {
		setState(prev => ({
			...prev,
			isOpen: false
		}));
	}, []);

	useEffect(() => {
		if (state.isOpen) {
			document.body.classList.add('modal-open');
			document.body.style.overflow = 'hidden';
		} else {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
		}

		return () => {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
		};
	}, [state.isOpen]);

	useEffect(() => {
		if (!state.isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				hideAlert();
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [state.isOpen, hideAlert]);

	const contextValue = useMemo<AlertDialogContextValue>(
		() => ({
			showAlert,
			hideAlert
		}),
		[showAlert, hideAlert]
	);

	const handleBackdropClick = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			if (event.target === event.currentTarget) {
				hideAlert();
			}
		},
		[hideAlert]
	);

	return (
		<AlertDialogContext.Provider value={contextValue}>
			{children}
			{state.isOpen && (
				<>
					<div
						className="modal fade show d-block"
						role="dialog"
						tabIndex={-1}
						aria-modal="true"
						onMouseDown={handleBackdropClick}
					>
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">{state.title}</h5>
									<button type="button" className="btn-close" aria-label="Close" onClick={hideAlert}></button>
								</div>
								<div className="modal-body">
									<p className="mb-0">{state.message}</p>
								</div>
								<div className="modal-footer">
									<button type="button" className="btn btn-primary" onClick={hideAlert}>
										{state.confirmLabel}
									</button>
								</div>
							</div>
						</div>
					</div>
					<div className="modal-backdrop fade show"></div>
				</>
			)}
		</AlertDialogContext.Provider>
	);
}

export function useAlertDialog() {
	const context = useContext(AlertDialogContext);
	if (!context) {
		throw new Error('useAlertDialog must be used within an AlertDialogProvider');
	}
	return context;
}


